import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays,
  Languages,
  Mail,
  Phone,
  User,
  UserRound,
} from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { ClientBlockChip } from '@/shared/ui/chips/ClientBlockChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { useClientDetail } from '@/entities/client';
import { formatDateTime } from '@/shared/utils/date';
import { FeaturesClientsBlockParcelCreationButton } from '@/features/clients';
import { usePerm } from '@/shared/hooks/usePerm';
import { ClaimedTrekIdsSection } from '@/widgets/client-detail/modules/ui/ClaimedTrekIdsSection';
import { PaymentHistorySection } from '@/widgets/client-detail/modules/ui/PaymentHistorySection';

const isFilled = (value: string | null | undefined): boolean => Boolean(value?.trim());

export const WidgetsClientDetailMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const query = useClientDetail(id);
  const canBlock = usePerm('clients.detail.block');
  const canUnblock = usePerm('clients.detail.unblock');
  const canViewClient = usePerm('clients.view');

  if (query.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <div className="p-6">
        <ErrorState
          message={extractErrorMessage(query.error, t('clients.detail.notFound'))}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const client = query.data;
  const displayName = isFilled(client.full_name) ? client.full_name : client.email;
  const languages = client.languages.filter((l) => l.trim().length > 0);

  const empty = (
    <span className="text-ink-faint italic">{t('clients.empty')}</span>
  );

  // Owner spec (2026-05-30): address / zip / PINFL (and the user-level limit
  // derived from PINFL) were dropped from the User model — recipient details
  // are collected per-parcel / per-recipient now, so they aren't shown on the
  // client profile page anymore.
  const rows: { key: string; icon: typeof Mail; label: string; value: ReactNode }[] = [
    {
      key: 'firstName',
      icon: User,
      label: t('clients.detail.fields.firstName'),
      value: isFilled(client.first_name) ? client.first_name : empty,
    },
    {
      key: 'lastName',
      icon: User,
      label: t('clients.detail.fields.lastName'),
      value: isFilled(client.last_name) ? client.last_name : empty,
    },
    {
      key: 'email',
      icon: Mail,
      label: t('clients.detail.fields.email'),
      value: isFilled(client.email) ? client.email : empty,
    },
    {
      key: 'phone',
      icon: Phone,
      label: t('clients.detail.fields.phone'),
      value: isFilled(client.phone_number) ? client.phone_number : empty,
    },
    {
      key: 'createdAt',
      icon: CalendarDays,
      label: t('clients.detail.fields.createdAt'),
      value: formatDateTime(client.created_at),
    },
  ];

  return (
    <>
      <PageHeader title={displayName} subtitle={t('clients.detail.subtitle')} showBack backTo="/clients" />
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <Section>
          <div className="relative">
            <div className="absolute right-0 top-0 z-10 max-w-[40%]">
              <ClientBlockChip blocked={client.is_parcel_creation_blocked} />
            </div>

            <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left md:gap-4">
              <span className="inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-ink-muted">
                {client.avatar_url ? (
                  <img src={client.avatar_url} alt="" className="h-full w-full object-cover" />
                ) : isFilled(client.full_name) ? (
                  <span className="text-2xl font-semibold uppercase">{client.full_name.charAt(0)}</span>
                ) : (
                  <UserRound className="h-8 w-8" aria-hidden />
                )}
              </span>
              <div className="mt-2 min-w-0 px-3 md:mt-0 md:flex-1 md:px-0 md:pr-32">
                <p className="break-words text-lg font-semibold text-ink">{displayName}</p>
                <p className="mt-1 break-words text-sm text-ink-muted">{client.email}</p>
              </div>
            </div>

            {((client.is_parcel_creation_blocked && canUnblock) ||
              (!client.is_parcel_creation_blocked && canBlock)) && (
              <div className="mt-4 flex justify-end border-t border-slate-100 pt-4">
                <FeaturesClientsBlockParcelCreationButton
                  clientId={client.id}
                  blocked={client.is_parcel_creation_blocked}
                />
              </div>
            )}
          </div>
        </Section>

        <Section title={t('clients.detail.infoSection')}>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {rows.map(({ key, icon: Icon, label, value }) => (
              <div key={key} className="flex items-start gap-3">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                <div className="min-w-0">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</dt>
                  <dd className="break-words text-sm text-ink-soft">{value}</dd>
                </div>
              </div>
            ))}

            <div className="flex items-start gap-3">
              <Languages className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
              <div className="min-w-0">
                <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                  {t('clients.detail.fields.languages')}
                </dt>
                <dd className="mt-1 text-sm text-ink-soft">
                  {languages.length > 0 ? (
                    <span className="flex flex-wrap gap-1.5">
                      {languages.map((lang) => (
                        <span
                          key={lang}
                          className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-soft"
                        >
                          {lang}
                        </span>
                      ))}
                    </span>
                  ) : (
                    empty
                  )}
                </dd>
              </div>
            </div>
          </dl>
        </Section>

        <ClaimedTrekIdsSection clientId={client.id} />

        {canViewClient && <PaymentHistorySection clientId={client.id} />}
      </div>
    </>
  );
};
